import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { ApiError } from '@vmgate/common';

const loginSchema = z.object({
  username: z.string().min(1, 'Username is required'),
  password: z.string().min(1, 'Password is required')
});

type LoginFormValues = z.infer<typeof loginSchema>;

export function LoginPage() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      username: '',
      password: ''
    }
  });

  useEffect(() => {
    const flash = sessionStorage.getItem('vmgate:adminAuthMessage');
    if (flash) {
      setInfo(flash);
      sessionStorage.removeItem('vmgate:adminAuthMessage');
    }
  }, []);

  useEffect(() => {
    if (user) {
      navigate('/', { replace: true });
    }
  }, [navigate, user]);

  const onSubmit = async (values: LoginFormValues) => {
    setError(null);
    try {
      await login(values);
      navigate('/');
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError('Invalid credentials. Please check username and password.');
      } else {
        setError('Unable to login at the moment. Please try again.');
      }
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #2563eb 100%)',
        padding: '2rem'
      }}
    >
      <div
        className="surface surface-padding"
        style={{
          width: '100%',
          maxWidth: '420px',
          borderRadius: '18px',
          boxShadow: '0 20px 45px rgba(15, 23, 42, 0.4)'
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <div
            style={{
              width: '56px',
              height: '56px',
              margin: '0 auto 1rem',
              borderRadius: '16px',
              display: 'grid',
              placeItems: 'center',
              background: 'rgba(37, 99, 235, 0.15)',
              color: 'var(--color-primary)',
              fontWeight: 600
            }}
          >
            VM
          </div>
          <h1 style={{ margin: 0, fontSize: '1.75rem' }}>VMGate Admin</h1>
          <p className="text-muted" style={{ margin: '0.5rem 0 0' }}>
            Sign in to manage customers, licenses, and devices.
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            <span style={{ fontWeight: 600 }}>Username</span>
            <input
              type="text"
              {...register('username')}
              autoComplete="username"
              placeholder="admin"
              style={{
                borderRadius: '10px',
                border: '1px solid var(--color-border)',
                padding: '0.65rem 0.85rem',
                fontSize: '1rem'
              }}
            />
            {errors.username && <span style={{ color: 'var(--color-danger)', fontSize: '0.85rem' }}>{errors.username.message}</span>}
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            <span style={{ fontWeight: 600 }}>Password</span>
            <input
              type="password"
              {...register('password')}
              autoComplete="current-password"
              placeholder="••••••••"
              style={{
                borderRadius: '10px',
                border: '1px solid var(--color-border)',
                padding: '0.65rem 0.85rem',
                fontSize: '1rem'
              }}
            />
            {errors.password && <span style={{ color: 'var(--color-danger)', fontSize: '0.85rem' }}>{errors.password.message}</span>}
          </label>

          {info && (
            <div
              style={{
                background: 'rgba(5, 150, 105, 0.12)',
                color: '#047857',
                borderRadius: '10px',
                padding: '0.75rem'
              }}
            >
              {info}
            </div>
          )}

          {error && (
            <div
              style={{
                background: 'rgba(220, 38, 38, 0.12)',
                color: '#b91c1c',
                borderRadius: '10px',
                padding: '0.75rem'
              }}
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            className="btn btn-primary"
            disabled={isSubmitting}
            style={{
              width: '100%',
              padding: '0.75rem',
              fontSize: '1rem',
              marginTop: '0.5rem'
            }}
          >
            {isSubmitting ? 'Signing in...' : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
  );
}

